/**
 * Native ProseMirror schema for the lesson-note editor.
 *
 * Composed from:
 *  - prosemirror-schema-basic    : paragraph, text, blockquote, code_block,
 *                                  hard_break, horizontal_rule, image, heading
 *  - prosemirror-schema-list     : ordered_list, bullet_list, list_item
 *  - prosemirror-tables          : table, table_row, table_header, table_cell
 *  - custom marks                : underline, strike, highlight, link
 *  - custom nodes                : task_list, task_item (rendered with a real
 *                                  <input type="checkbox"> via a NodeView)
 *
 * The schema's serialised JSON is the canonical document format stored on
 * `LessonNote.body`. Semantic class names start with `pm-`.
 */
import OrderedMap from 'orderedmap';
import { Schema, type NodeSpec, type MarkSpec } from 'prosemirror-model';
import { nodes as basicNodes, marks as basicMarks } from 'prosemirror-schema-basic';
import { addListNodes } from 'prosemirror-schema-list';
import { tableNodes } from 'prosemirror-tables';

// ----------------------------------------------------------------------------
// Heading (override basic's to clamp to 1-3 and tag the DOM class)
// ----------------------------------------------------------------------------
const heading: NodeSpec = {
  attrs: { level: { default: 1 } },
  content: 'inline*',
  group: 'block',
  defining: true,
  parseDOM: [
    { tag: 'h1', attrs: { level: 1 } },
    { tag: 'h2', attrs: { level: 2 } },
    { tag: 'h3', attrs: { level: 3 } },
  ],
  toDOM: (node) => [`h${node.attrs.level}`, { class: `pm-h${node.attrs.level}` }, 0],
};

// ----------------------------------------------------------------------------
// Image (with `src`, `alt`, `title`, and a transient `dataLoading` flag for
// the inline paste/drop upload UX).
// ----------------------------------------------------------------------------
const image: NodeSpec = {
  inline: false,
  group: 'block',
  draggable: true,
  attrs: {
    src: { default: '' },
    alt: { default: null },
    title: { default: null },
    dataLoading: { default: null },
  },
  parseDOM: [{
    tag: 'img[src]',
    getAttrs: (dom) => {
      const el = dom as HTMLImageElement;
      return {
        src: el.getAttribute('src'),
        alt: el.getAttribute('alt'),
        title: el.getAttribute('title'),
        dataLoading: el.getAttribute('data-loading'),
      };
    },
  }],
  toDOM(node) {
    const { src, alt, title, dataLoading } = node.attrs;
    const attrs: Record<string, string> = { src, class: 'pm-image' };
    if (alt) attrs.alt = alt;
    if (title) attrs.title = title;
    if (dataLoading) attrs['data-loading'] = String(dataLoading);
    return ['img', attrs];
  },
};

// ----------------------------------------------------------------------------
// Task list / item — checkboxes that are part of the document.
// ----------------------------------------------------------------------------
const taskList: NodeSpec = {
  group: 'block',
  content: 'task_item+',
  parseDOM: [{ tag: 'ul[data-type="task_list"]' }],
  toDOM: () => ['ul', { 'data-type': 'task_list', class: 'pm-task-list' }, 0],
};

const taskItem: NodeSpec = {
  attrs: { checked: { default: false } },
  content: 'paragraph block*',
  defining: true,
  parseDOM: [{
    tag: 'li[data-type="task_item"]',
    getAttrs: (dom) => ({
      checked: (dom as HTMLElement).getAttribute('data-checked') === 'true',
    }),
  }],
  toDOM: (node) => [
    'li',
    {
      'data-type': 'task_item',
      'data-checked': node.attrs.checked ? 'true' : 'false',
      class: 'pm-task-item',
    },
    0,
  ],
};

// ----------------------------------------------------------------------------
// Marks
// ----------------------------------------------------------------------------
const underline: MarkSpec = {
  parseDOM: [
    { tag: 'u' },
    { style: 'text-decoration=underline' },
  ],
  toDOM: () => ['u', 0],
};

const strike: MarkSpec = {
  parseDOM: [
    { tag: 's' },
    { tag: 'strike' },
    { tag: 'del' },
    { style: 'text-decoration=line-through' },
  ],
  toDOM: () => ['s', 0],
};

const highlight: MarkSpec = {
  parseDOM: [{ tag: 'mark' }],
  toDOM: () => ['mark', { class: 'pm-highlight' }, 0],
};

// Override basic's link mark to include `target` + nofollow rel.
const link: MarkSpec = {
  attrs: {
    href: {},
    title: { default: null },
    target: { default: '_blank' },
  },
  inclusive: false,
  parseDOM: [{
    tag: 'a[href]',
    getAttrs: (dom) => ({
      href: (dom as HTMLElement).getAttribute('href'),
      title: (dom as HTMLElement).getAttribute('title'),
      target: (dom as HTMLElement).getAttribute('target') || '_blank',
    }),
  }],
  toDOM: (mark) => [
    'a',
    {
      href: mark.attrs.href,
      title: mark.attrs.title,
      target: mark.attrs.target,
      rel: 'noopener noreferrer nofollow',
      class: 'pm-link',
    },
    0,
  ],
};

// ----------------------------------------------------------------------------
// Assemble the schema.
//
// addListNodes expects an OrderedMap<NodeSpec>. Current versions of
// prosemirror-schema-basic export `nodes` as a plain object literal, NOT an
// OrderedMap — so we must wrap it with OrderedMap.from() before calling
// .update() (otherwise the runtime blows up with `update is not a function`).
// Order of node maps matters — list_item must live in the block group for
// nested lists to work, which is exactly what addListNodes wires up.
// ----------------------------------------------------------------------------

// Start from basic, override heading + image.
let nodes: OrderedMap<NodeSpec> = OrderedMap.from<NodeSpec>(basicNodes)
  .update('heading', heading)
  .update('image', image);

// Append list nodes (`ordered_list`, `bullet_list`, `list_item`).
nodes = addListNodes(nodes, 'paragraph block*', 'block');

// Append table nodes.
const tNodes = tableNodes({
  tableGroup: 'block',
  cellContent: 'block+',
  cellAttributes: {
    background: {
      default: null,
      getFromDOM: (dom) => (dom as HTMLElement).style.backgroundColor || null,
      setDOMAttr: (value, attrs) => {
        if (value) attrs.style = `${attrs.style || ''}background-color: ${value};`;
      },
    },
  },
});
for (const name of Object.keys(tNodes)) {
  nodes = nodes.addToEnd(name, tNodes[name]);
}

// Append task list nodes.
nodes = nodes.addToEnd('task_list', taskList).addToEnd('task_item', taskItem);

// Marks — basic + ours. Same OrderedMap.from() wrap as nodes above, for the
// same reason: prosemirror-schema-basic exports `marks` as a plain object.
let marks: OrderedMap<MarkSpec> = OrderedMap.from<MarkSpec>(basicMarks);
marks = marks.update('link', link);
marks = marks.addToEnd('underline', underline);
marks = marks.addToEnd('strike', strike);
marks = marks.addToEnd('highlight', highlight);

export const pmSchema = new Schema({ nodes, marks });

/** A canonical empty doc. Round-trips through the schema. */
export const EMPTY_DOC_JSON = { type: 'doc', content: [{ type: 'paragraph' }] };
